import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Users, FileCheck2, DollarSign, KanbanSquare, Store, UserPlus, TrendingUp, Building2, Mail, RefreshCw, CreditCard, XCircle, Undo2, Clock, ReceiptText, ChevronDown, Activity, Database, Zap, Radio, Send, Sparkles, ShieldAlert, FileText, Search, LayoutDashboard, IdCard, Award, MapPin, Briefcase, ShieldCheck, HardHat, ChevronRight, ChevronLeft, Check } from "lucide-react";

const fmt = (n) => "$" + Number(n || 0).toLocaleString();
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" }); } catch { return ""; } };
const fmtDateLong = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); } catch { return ""; } };
const fmtRelHours = (h) => {
  if (h === null || h === undefined) return "Never";
  if (h < 1) return `${Math.round(h * 60)} min ago`;
  if (h < 24) return `${h.toFixed(1)} h ago`;
  return `${Math.round(h / 24)} d ago`;
};

function PlatformHealth({ stats, onRefreshFeed, warming }) {
  const [testing, setTesting] = React.useState(false);
  const sendTestDigest = async () => {
    setTesting(true);
    try {
      const r = await api.post("/admin/test-health-digest");
      toast.success(`Digest sent to ${r.data.to}. Check your inbox.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send test digest."); }
    finally { setTesting(false); }
  };

  if (!stats) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6" data-testid="platform-health-loading">
        <div className="h-5 w-40 bg-slate-100 rounded animate-pulse mb-4" />
        <div className="h-24 bg-slate-50 rounded animate-pulse" />
      </div>
    );
  }
  const kpis = [
    { label: "Signups today", value: stats.signups_today, sub: `${stats.signups_week} this week`, icon: UserPlus, color: "text-accent", testid: "kpi-signups" },
    { label: "AI analyses today", value: stats.ai_analyses_today, sub: `${stats.ai_analyses_week} this week`, icon: Zap, color: "text-amber-600", testid: "kpi-ai-usage" },
    { label: "SAM feed", value: (stats.sam_feed?.opp_count || 0).toLocaleString(), sub: `Refreshed ${fmtRelHours(stats.sam_feed?.hours_since_refresh)}`, icon: Radio, color: "text-emerald-600", testid: "kpi-sam-feed" },
    { label: "Founding 100", value: `${stats.founding?.taken || 0}/${stats.founding?.total || 100}`, sub: `${stats.founding?.remaining ?? 100} seats left`, icon: Sparkles, color: "text-lime-600", testid: "kpi-founding" },
  ];
  const collections = Object.entries(stats.collection_sizes || {});
  const stale = stats.sam_feed?.hours_since_refresh !== null && stats.sam_feed?.hours_since_refresh > 30;

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6" data-testid="platform-health">
      <div className="flex items-center justify-between gap-4 mb-5 flex-wrap">
        <h3 className="font-heading text-lg font-semibold inline-flex items-center gap-2"><Activity className="h-5 w-5" /> Platform health</h3>
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-xs text-slate-400">Emailed daily 13:00 UTC · rolling 7-day window</span>
          <button onClick={sendTestDigest} disabled={testing} data-testid="send-test-digest-btn" className="inline-flex items-center gap-1.5 text-xs font-medium rounded-md px-2.5 py-1.5 bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition-colors">
            <Send className={`h-3.5 w-3.5 ${testing ? "animate-pulse" : ""}`} /> {testing ? "Sending…" : "Send test digest"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {kpis.map((k) => (
          <div key={k.label} className="rounded-lg border border-slate-100 bg-slate-50 p-4" data-testid={k.testid}>
            <div className="flex items-center gap-2 mb-1">
              <k.icon className={`h-4 w-4 ${k.color}`} />
              <span className="text-xs uppercase tracking-wider text-slate-500 font-medium">{k.label}</span>
            </div>
            <p className="font-data text-2xl font-medium text-slate-900">{k.value}</p>
            <p className="text-xs text-slate-500 mt-0.5">{k.sub}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div data-testid="top-ai-users">
          <h4 className="text-sm font-semibold text-slate-700 mb-3 inline-flex items-center gap-2"><Zap className="h-4 w-4 text-amber-500" /> Top AI users today</h4>
          {(!stats.top_ai_users || stats.top_ai_users.length === 0) ? (
            <p className="text-sm text-slate-400">No AI usage yet today.</p>
          ) : (
            <div className="divide-y divide-slate-100">
              {stats.top_ai_users.map((u, i) => (
                <div key={u.email || i} className="flex items-center justify-between py-2" data-testid={`top-ai-user-${i}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{u.email || "unknown"}</p>
                    <p className="text-xs text-slate-400 capitalize">{u.plan || "—"}</p>
                  </div>
                  <span className="font-data text-sm font-medium text-amber-600 shrink-0">{u.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div data-testid="collection-sizes">
          <h4 className="text-sm font-semibold text-slate-700 mb-3 inline-flex items-center gap-2"><Database className="h-4 w-4 text-slate-500" /> Collection sizes</h4>
          <div className="grid grid-cols-2 gap-2">
            {collections.map(([name, count]) => (
              <div key={name} className="flex items-center justify-between rounded-md border border-slate-100 bg-slate-50 px-3 py-2" data-testid={`collection-${name}`}>
                <span className="text-xs text-slate-500 truncate">{name}</span>
                <span className="font-data text-sm font-medium text-slate-900">{Number(count).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {stale && (
        <div className="mt-5 flex items-center justify-between gap-3 rounded-md bg-amber-50 border border-amber-200 px-3 py-2" data-testid="sam-stale-warning">
          <p className="text-xs text-amber-800">SAM feed hasn&apos;t refreshed in over 30 hours — the daily cron may have failed.</p>
          <button onClick={onRefreshFeed} disabled={warming} data-testid="stale-refresh-btn" className="text-xs font-medium rounded-md px-2.5 py-1.5 bg-white border border-amber-300 text-amber-700 hover:bg-amber-100 disabled:opacity-50 transition-colors inline-flex items-center gap-1">
            <RefreshCw className={`h-3.5 w-3.5 ${warming ? "animate-spin" : ""}`} /> Refresh now
          </button>
        </div>
      )}
    </div>
  );
}


const ACTION_META = {
  cancel_end_of_period: { label: "Cancel (end of period)", color: "bg-slate-100 text-slate-600 border-slate-200", icon: XCircle },
  cancel_immediate: { label: "Cancel immediately", color: "bg-red-50 text-red-700 border-red-200", icon: XCircle },
  refund: { label: "Refund", color: "bg-amber-50 text-amber-700 border-amber-200", icon: Undo2 },
  refund_and_revoke: { label: "Refund & revoke", color: "bg-red-100 text-red-800 border-red-300", icon: ShieldAlert },
};
const fmtRelTime = (iso) => {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.round(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)} h ago`;
  return `${Math.round(diff / 86400)} d ago`;
};

function ActionsLog({ actions, onReload }) {
  const [collapsed, setCollapsed] = React.useState(false);
  const exportCSV = () => {
    const header = "when,action,admin_email,target_email,amount,reason,founder_seat_released";
    const rows = actions.map((a) => {
      const amt = a.result?.refunded?.amount ?? a.result?.amount ?? "";
      const seat = a.result?.founder_seat_released ? `#${String(a.result?.released_founder_number || 0).padStart(3, "0")}` : "";
      return [a.created_at, a.action, a.admin_email, a.target_email, amt, `"${(a.reason || "").replace(/"/g, "'")}"`, seat].join(",");
    });
    const blob = new Blob([[header, ...rows].join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `bidmatchai-admin-actions-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6" data-testid="admin-actions-log">
      <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
        <h3 className="font-heading text-lg font-semibold inline-flex items-center gap-2">
          <FileText className="h-5 w-5" /> Admin activity log <span className="text-sm font-normal text-slate-400">({actions.length})</span>
        </h3>
        <div className="flex items-center gap-2">
          <button onClick={onReload} data-testid="actions-refresh" className="text-xs font-medium rounded-md px-2.5 py-1.5 bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors inline-flex items-center gap-1">
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
          <button onClick={exportCSV} disabled={!actions.length} data-testid="actions-export-csv" className="text-xs font-medium rounded-md px-2.5 py-1.5 bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition-colors">
            Export CSV
          </button>
          <button onClick={() => setCollapsed((c) => !c)} data-testid="actions-toggle" className="text-xs font-medium rounded-md px-2.5 py-1.5 bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors inline-flex items-center gap-1">
            {collapsed ? "Show" : "Hide"} <ChevronDown className={`h-3.5 w-3.5 transition-transform ${collapsed ? "" : "rotate-180"}`} />
          </button>
        </div>
      </div>
      {!collapsed && (
        actions.length === 0 ? (
          <p className="text-sm text-slate-400 py-3">No admin destructive actions logged yet. This log captures every cancel, refund, and refund-and-revoke — kept for dispute defence and chargeback records.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wider text-slate-400 border-b border-slate-200">
                  <th className="text-left font-medium py-2 pr-3">When</th>
                  <th className="text-left font-medium py-2 pr-3">Action</th>
                  <th className="text-left font-medium py-2 pr-3">Target user</th>
                  <th className="text-right font-medium py-2 pr-3">Amount</th>
                  <th className="text-left font-medium py-2 pr-3">Reason</th>
                  <th className="text-left font-medium py-2">Admin</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {actions.map((a, i) => {
                  const meta = ACTION_META[a.action] || { label: a.action, color: "bg-slate-100 text-slate-600 border-slate-200", icon: Clock };
                  const Icon = meta.icon;
                  const amount = a.result?.refunded?.amount ?? a.result?.amount ?? null;
                  const seat = a.result?.founder_seat_released ? ` · Founder #${String(a.result?.released_founder_number || 0).padStart(3, "0")}` : "";
                  return (
                    <tr key={i} data-testid={`action-row-${i}`} className="align-top">
                      <td className="py-3 pr-3 text-slate-500 whitespace-nowrap" title={a.created_at}>{fmtRelTime(a.created_at)}</td>
                      <td className="py-3 pr-3">
                        <span className={`inline-flex items-center gap-1 text-xs font-medium border rounded-full px-2.5 py-0.5 ${meta.color}`}>
                          <Icon className="h-3 w-3" /> {meta.label}
                        </span>
                        {seat && <span className="text-[11px] text-emerald-700 ml-2">{seat}</span>}
                      </td>
                      <td className="py-3 pr-3 text-slate-900 truncate max-w-[220px]" title={a.target_email}>{a.target_email || "—"}</td>
                      <td className="py-3 pr-3 text-right font-data text-slate-900 whitespace-nowrap">{amount !== null ? fmt(amount) : "—"}</td>
                      <td className="py-3 pr-3 text-slate-600 max-w-xs truncate" title={a.reason}>{a.reason || <span className="text-slate-300">—</span>}</td>
                      <td className="py-3 text-slate-500 truncate max-w-[180px]" title={a.admin_email}>{a.admin_email}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}


function ProfilesTab() {
  const [q, setQ] = useState("");
  const [qDeb, setQDeb] = useState("");
  const [plan, setPlan] = useState("all");
  const [hasProfile, setHasProfile] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState("");
  const [nudgingId, setNudgingId] = useState("");
  const [bulkNudging, setBulkNudging] = useState(false);
  const [bump, setBump] = useState(0);  // triggers re-fetch after nudge sends

  // debounce search input
  useEffect(() => {
    const t = setTimeout(() => { setQDeb(q); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    const params = new URLSearchParams();
    if (qDeb) params.set("q", qDeb);
    if (plan && plan !== "all") params.set("plan", plan);
    if (hasProfile) params.set("has_profile", hasProfile);
    params.set("page", page);
    params.set("limit", 25);
    api.get(`/admin/profiles?${params.toString()}`)
      .then((r) => { if (!cancel) setData(r.data); })
      .catch((e) => { if (!cancel) toast.error(e?.response?.data?.detail || "Could not load profiles."); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [qDeb, plan, hasProfile, page, bump]);

  const nudgeOne = async (u) => {
    if (!u?.user_id) return;
    setNudgingId(u.user_id);
    try {
      const { data: res } = await api.post("/admin/nudge-profile", { user_ids: [u.user_id] });
      if (res.sent > 0) toast.success(`Nudge sent to ${u.email}`);
      else if (res.rate_limited > 0) toast.error(`${u.email} was nudged recently — try again in a few days.`);
      else if (res.errors > 0) toast.error("Email send failed — check Resend logs.");
      else toast.error("No email sent (user may already have a profile).");
      setBump((b) => b + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Nudge failed.");
    } finally {
      setNudgingId("");
    }
  };

  const nudgeAllMissing = async () => {
    const missing = totals?.without_profile ?? 0;
    if (!missing) { toast.error("Everyone already has a profile."); return; }
    if (!window.confirm(`Send a nudge email to all ${missing} users who haven't completed their profile? (Users nudged in the last 72h will be skipped.)`)) return;
    setBulkNudging(true);
    try {
      const { data: res } = await api.post("/admin/nudge-profile", { all_missing: true });
      toast.success(`Sent ${res.sent} · Skipped (recently nudged): ${res.rate_limited} · Errors: ${res.errors}`);
      setBump((b) => b + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk nudge failed.");
    } finally {
      setBulkNudging(false);
    }
  };

  const exportCSV = () => {
    const rows = data?.rows || [];
    if (!rows.length) { toast.error("Nothing to export."); return; }
    const header = ["email","name","plan","subscription_status","trial_status","signed_up","has_profile","company_name","trade","state","years_in_business","annual_revenue","bonding_capacity","employee_count","gov_contracts_completed","naics_codes","certifications","has_license","emr","readiness_score"].join(",");
    const q2 = (v) => `"${String(v ?? "").replace(/"/g,"'")}"`;
    const lines = rows.map((row) => {
      const u = row.user || {};
      const p = row.profile || {};
      return [
        u.email, u.name, u.plan, u.subscription_status, u.trial_status, u.created_at,
        row.has_profile ? "yes" : "no",
        p.company_name, p.trade, p.state, p.years_in_business, p.annual_revenue,
        p.bonding_capacity, p.employee_count, p.gov_contracts_completed,
        (p.naics_codes || []).join("|"), (p.certifications || []).join("|"),
        p.has_license ? "yes" : "no", p.emr,
        row.readiness?.score,
      ].map(q2).join(",");
    });
    const blob = new Blob([[header, ...lines].join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `bidmatchai-profiles-page${page}-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const totals = data?.totals || {};
  const rows = data?.rows || [];

  return (
    <div className="space-y-6" data-testid="profiles-tab">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="profile-total-users">
          <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">All signups</p>
          <p className="font-data text-2xl font-medium text-slate-900">{totals.all_users ?? "—"}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="profile-with-profile">
          <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">With profile filled</p>
          <p className="font-data text-2xl font-medium text-emerald-600">{totals.with_profile ?? "—"}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="profile-without-profile">
          <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">Not yet completed</p>
          <div className="flex items-end justify-between gap-2">
            <p className="font-data text-2xl font-medium text-amber-600">{totals.without_profile ?? "—"}</p>
            {(totals.without_profile ?? 0) > 0 && (
              <button
                onClick={nudgeAllMissing}
                disabled={bulkNudging}
                data-testid="nudge-all-missing"
                className="text-xs font-medium rounded-md px-2.5 py-1.5 bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-60 transition-colors inline-flex items-center gap-1.5"
              >
                {bulkNudging ? <div className="h-3 w-3 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <Send className="h-3 w-3" />}
                {bulkNudging ? "Sending…" : "Nudge all"}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
        <div className="flex items-center gap-3 flex-wrap mb-4">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search email, name, or company…"
              data-testid="profiles-search-input"
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-accent/40"
            />
          </div>
          <select value={plan} onChange={(e) => { setPlan(e.target.value); setPage(1); }} data-testid="profiles-plan-filter" className="text-sm border border-slate-200 rounded-md px-2.5 py-2 bg-white text-slate-700">
            <option value="all">All plans</option>
            <option value="free">Free</option>
            <option value="growth">Growth</option>
            <option value="pro">Pro</option>
            <option value="enterprise">Enterprise</option>
          </select>
          <select value={hasProfile} onChange={(e) => { setHasProfile(e.target.value); setPage(1); }} data-testid="profiles-completeness-filter" className="text-sm border border-slate-200 rounded-md px-2.5 py-2 bg-white text-slate-700">
            <option value="">Any profile status</option>
            <option value="yes">Profile completed</option>
            <option value="no">Profile empty</option>
          </select>
          <button onClick={exportCSV} disabled={!rows.length} data-testid="profiles-export-csv" className="text-xs font-medium rounded-md px-3 py-2 bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition-colors">
            Export CSV
          </button>
        </div>

        {loading && !rows.length ? (
          <div className="h-40 flex items-center justify-center"><div className="h-8 w-8 border-[3px] border-slate-200 border-t-slate-900 rounded-full animate-spin" /></div>
        ) : !rows.length ? (
          <p className="text-sm text-slate-400 py-8 text-center">No users match those filters.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wider text-slate-400 border-b border-slate-200">
                  <th className="text-left font-medium py-2 pr-3">User</th>
                  <th className="text-left font-medium py-2 pr-3">Plan</th>
                  <th className="text-left font-medium py-2 pr-3">Company</th>
                  <th className="text-left font-medium py-2 pr-3">Trade</th>
                  <th className="text-left font-medium py-2 pr-3">State</th>
                  <th className="text-right font-medium py-2 pr-3">Readiness</th>
                  <th className="text-left font-medium py-2 pr-3">Signed up</th>
                  <th className="text-left font-medium py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((row, i) => {
                  const u = row.user || {};
                  const p = row.profile || {};
                  const isOpen = openId === u.user_id;
                  const score = row.readiness?.score;
                  const scoreColor = score >= 800 ? "text-emerald-600" : score >= 500 ? "text-amber-600" : score >= 300 ? "text-orange-600" : "text-slate-400";
                  return (
                    <React.Fragment key={u.user_id || i}>
                      <tr data-testid={`profile-row-${i}`} className="align-middle hover:bg-slate-50/60 cursor-pointer" onClick={() => setOpenId(isOpen ? "" : u.user_id)}>
                        <td className="py-3 pr-3 min-w-0 max-w-[240px]">
                          <p className="text-sm font-medium text-slate-900 truncate" title={u.name}>{u.name || u.email}</p>
                          <p className="text-xs text-slate-400 truncate" title={u.email}>{u.email}</p>
                          {u.is_founder && <p className="text-[10px] text-lime-700 font-semibold mt-0.5">Founder #{String(u.founder_number || 0).padStart(3, "0")}</p>}
                        </td>
                        <td className="py-3 pr-3">
                          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 capitalize">{u.plan || "free"}</span>
                          {u.has_payment_method && (
                            <p className="text-[10px] text-emerald-700 font-medium mt-0.5 inline-flex items-center gap-1" title={`Card on file: ${u.card_brand || "card"} ending ${u.card_last4 || ""}`} data-testid={`profile-row-card-${i}`}>
                              <CreditCard className="h-3 w-3" /> Card on file
                            </p>
                          )}
                        </td>
                        <td className="py-3 pr-3 text-slate-700 truncate max-w-[180px]">{p.company_name || <span className="text-slate-300">—</span>}</td>
                        <td className="py-3 pr-3 text-slate-600 truncate max-w-[140px]">{p.trade || <span className="text-slate-300">—</span>}</td>
                        <td className="py-3 pr-3 text-slate-600">{p.state || <span className="text-slate-300">—</span>}</td>
                        <td className="py-3 pr-3 text-right whitespace-nowrap">
                          {row.has_profile ? (
                            <span className={`font-data font-medium ${scoreColor}`}>{score ?? "—"}<span className="text-xs text-slate-400">/1000</span></span>
                          ) : (
                            <span className="text-xs text-slate-400">Not started</span>
                          )}
                        </td>
                        <td className="py-3 pr-3 text-xs text-slate-500 whitespace-nowrap" title={u.created_at}>{fmtDate(u.created_at)}</td>
                        <td className="py-3 text-right">
                          <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                        </td>
                      </tr>
                      {isOpen && (
                        <tr data-testid={`profile-detail-${i}`}>
                          <td colSpan={8} className="bg-slate-50/70 px-4 py-5">
                            {!row.has_profile ? (
                              <div className="text-sm text-slate-600 py-4 text-center max-w-xl mx-auto">
                                <IdCard className="h-8 w-8 mx-auto text-slate-300 mb-2" />
                                <p className="mb-3">This user hasn&apos;t filled their Profile Builder yet. Send them a nudge — they&apos;ll unlock the AI readiness score, opportunity matching, and win-probability scoring once they complete it.</p>
                                {u.last_profile_nudge_at && (
                                  <p className="text-xs text-slate-400 mb-3" data-testid={`profile-last-nudge-${i}`}>
                                    Last nudged {fmtDate(u.last_profile_nudge_at)}{u.profile_nudge_count > 1 ? ` · ${u.profile_nudge_count} nudges total` : ""}
                                  </p>
                                )}
                                <button
                                  onClick={(e) => { e.stopPropagation(); nudgeOne(u); }}
                                  disabled={nudgingId === u.user_id}
                                  data-testid={`nudge-user-${i}`}
                                  className="inline-flex items-center gap-2 bg-emerald-900 text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-emerald-800 disabled:opacity-60 transition-colors"
                                >
                                  {nudgingId === u.user_id ? <div className="h-3.5 w-3.5 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                                  {nudgingId === u.user_id ? "Sending…" : "Send profile nudge email"}
                                </button>
                              </div>
                            ) : (
                              <ProfileDetail user={u} profile={p} readiness={row.readiness} />
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {data && data.pages > 1 && (
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
            <p className="text-xs text-slate-500" data-testid="profiles-pagination-info">
              Page {data.page} of {data.pages} · {data.total} result{data.total === 1 ? "" : "s"}
            </p>
            <div className="flex gap-2">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} data-testid="profiles-prev-page" className="text-xs font-medium rounded-md px-3 py-1.5 bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50 inline-flex items-center gap-1">
                <ChevronLeft className="h-3.5 w-3.5" /> Prev
              </button>
              <button onClick={() => setPage((p) => Math.min(data.pages, p + 1))} disabled={page >= data.pages} data-testid="profiles-next-page" className="text-xs font-medium rounded-md px-3 py-1.5 bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50 inline-flex items-center gap-1">
                Next <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function BillingSummary({ user }) {
  const u = user || {};
  const hasCard = !!u.has_payment_method;
  const isTrialing = u.stripe_status === "trialing";
  const isActive = u.subscription_status === "active";
  const isCanceling = u.cancel_at_period_end;
  const paidStart = u.paid_starts_at || u.trial_end;
  const interval = u.stripe_interval === "year" ? "annual" : u.stripe_interval === "month" ? "monthly" : "";

  // Status label for the badge on the right
  let statusLabel = "No subscription";
  let statusColor = "bg-slate-100 text-slate-500 border-slate-200";
  if (isActive && isTrialing) { statusLabel = "Free trial"; statusColor = "bg-amber-50 text-amber-700 border-amber-200"; }
  else if (isActive) { statusLabel = "Paid · Active"; statusColor = "bg-emerald-50 text-emerald-700 border-emerald-200"; }
  else if (u.subscription_status === "canceled") { statusLabel = "Canceled"; statusColor = "bg-red-50 text-red-700 border-red-200"; }
  else if (u.subscription_status === "expired") { statusLabel = "Expired"; statusColor = "bg-red-50 text-red-700 border-red-200"; }

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="billing-summary">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <p className="text-xs uppercase tracking-wider text-slate-400 inline-flex items-center gap-1.5"><CreditCard className="h-3.5 w-3.5" /> Billing</p>
        <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full border ${statusColor}`}>{statusLabel}{interval ? ` · ${interval}` : ""}</span>
      </div>
      <div className="grid sm:grid-cols-2 gap-4 text-sm">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Card on file</p>
          {hasCard ? (
            <p className="text-slate-900 inline-flex items-center gap-2 flex-wrap" data-testid="billing-card-line">
              <Check className="h-4 w-4 text-emerald-600" strokeWidth={3} />
              <span className="capitalize font-medium">{u.card_brand || "Card"}</span>
              <span className="text-slate-500">ending</span>
              <span className="font-data">{u.card_last4 || "••••"}</span>
              {u.card_exp_month && u.card_exp_year && (
                <span className="text-xs text-slate-400">exp {String(u.card_exp_month).padStart(2, "0")}/{String(u.card_exp_year).slice(-2)}</span>
              )}
            </p>
          ) : (
            <p className="text-slate-400 inline-flex items-center gap-2" data-testid="billing-no-card">
              <XCircle className="h-4 w-4 text-slate-300" /> Not on file
            </p>
          )}
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">
            {isTrialing ? "Paid subscription starts" : isActive ? (isCanceling ? "Access ends" : "Next charge") : "Subscription"}
          </p>
          {isActive ? (
            <p className="text-slate-900 font-medium" data-testid="billing-paid-start">
              {isTrialing
                ? (paidStart ? fmtDateLong(paidStart) : <span className="text-slate-400">—</span>)
                : (u.current_period_end ? fmtDateLong(u.current_period_end) : <span className="text-slate-400">—</span>)}
              {isCanceling && <span className="text-xs text-red-600 ml-2 font-normal">(canceled)</span>}
            </p>
          ) : (
            <p className="text-slate-400">—</p>
          )}
        </div>
      </div>
    </div>
  );
}


function ProfileDetail({ user, profile, readiness }) {
  const p = profile || {};
  const naics = (p.naics_codes || []).filter(Boolean);
  const certs = (p.certifications || []).filter(Boolean);
  const past = (p.past_projects || []).filter(Boolean);
  const money = (v) => (v ? "$" + Number(v).toLocaleString() : "—");

  const stats = [
    { icon: Briefcase, label: "Years in business", value: p.years_in_business || "—" },
    { icon: DollarSign, label: "Annual revenue", value: money(p.annual_revenue) },
    { icon: ShieldCheck, label: "Bonding capacity", value: money(p.bonding_capacity) },
    { icon: HardHat, label: "Employees", value: p.employee_count || "—" },
    { icon: Award, label: "Gov contracts done", value: p.gov_contracts_completed || 0 },
    { icon: MapPin, label: "State", value: p.state || "—" },
  ];

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-400">Company</p>
          <h4 className="font-heading text-lg font-semibold text-slate-900">{p.company_name || <span className="text-slate-400">Not set</span>}</h4>
          <p className="text-sm text-slate-500 mt-0.5">{p.trade || "—"}{p.state ? ` · ${p.state}` : ""}</p>
          {p.website && <a href={p.website.startsWith("http") ? p.website : `https://${p.website}`} target="_blank" rel="noreferrer" className="text-xs text-emerald-700 hover:underline">{p.website}</a>}
        </div>
        {readiness && (
          <div className="text-right shrink-0" data-testid="profile-readiness-badge">
            <p className="text-xs uppercase tracking-wider text-slate-400">Readiness</p>
            <p className="font-data text-3xl font-medium text-slate-900">{readiness.score}<span className="text-base text-slate-400">/1000</span></p>
            <p className="text-xs text-slate-500">{readiness.tier}</p>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {stats.map((s) => (
          <div key={s.label} className="bg-white border border-slate-200 rounded-lg p-3">
            <s.icon className="h-4 w-4 text-slate-400 mb-1" />
            <p className="text-[10px] uppercase tracking-wider text-slate-400">{s.label}</p>
            <p className="font-data text-sm font-medium text-slate-900 truncate" title={String(s.value)}>{s.value}</p>
          </div>
        ))}
      </div>

      <BillingSummary user={user} />

      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-400 mb-2">Licensing & compliance</p>
          <div className="space-y-1.5 text-sm">
            <p><span className="text-slate-500">License:</span> {p.has_license ? <span className="text-emerald-700 font-medium">Yes{p.license_number ? ` · ${p.license_number}` : ""}</span> : <span className="text-slate-400">No</span>}</p>
            <p><span className="text-slate-500">General liability:</span> {p.has_general_liability ? <span className="text-emerald-700">Yes</span> : <span className="text-slate-400">No</span>}</p>
            <p><span className="text-slate-500">Workers&apos; comp:</span> {p.has_workers_comp ? <span className="text-emerald-700">Yes</span> : <span className="text-slate-400">No</span>}</p>
            <p><span className="text-slate-500">EMR:</span> <span className="font-data">{p.emr ?? "—"}</span></p>
            <p><span className="text-slate-500">OSHA incidents (last yr):</span> <span className="font-data">{p.osha_incidents_last_year ?? 0}</span></p>
            {p.uei && <p><span className="text-slate-500">UEI:</span> <span className="font-data">{p.uei}</span></p>}
          </div>
        </div>

        <div>
          <p className="text-xs uppercase tracking-wider text-slate-400 mb-2">NAICS & certifications</p>
          {naics.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {naics.map((n) => <span key={n} className="text-[11px] font-data px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">{n}</span>)}
            </div>
          )}
          {certs.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {certs.map((c) => <span key={c} className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">{c}</span>)}
            </div>
          ) : (
            <p className="text-sm text-slate-400">No certifications reported.</p>
          )}
          {p.union_status && <p className="text-xs text-slate-500 mt-2 capitalize">Union status: {p.union_status}</p>}
          {p.service_areas && <p className="text-xs text-slate-500 mt-1">Service areas: {p.service_areas}</p>}
        </div>
      </div>

      {(p.equipment || p.client_references || past.length > 0) && (
        <div className="grid md:grid-cols-2 gap-5">
          {p.equipment && (
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-400 mb-2">Equipment</p>
              <p className="text-sm text-slate-600 whitespace-pre-line">{p.equipment}</p>
            </div>
          )}
          {p.client_references && (
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-400 mb-2">References</p>
              <p className="text-sm text-slate-600 whitespace-pre-line">{p.client_references}</p>
            </div>
          )}
          {past.length > 0 && (
            <div className="md:col-span-2">
              <p className="text-xs uppercase tracking-wider text-slate-400 mb-2">Past projects ({past.length})</p>
              <div className="space-y-1.5">
                {past.slice(0, 5).map((pr, idx) => (
                  <div key={idx} className="text-sm text-slate-600 flex flex-wrap gap-x-3 gap-y-0.5">
                    <span className="font-medium text-slate-800">{pr.title || "Untitled"}</span>
                    {pr.client && <span className="text-slate-500">· {pr.client}</span>}
                    {pr.value && <span className="text-slate-500">· ${Number(pr.value).toLocaleString()}</span>}
                    {pr.year && <span className="text-slate-400">· {pr.year}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="text-[11px] text-slate-400 pt-2 border-t border-slate-100">
        User ID: <span className="font-data">{user.user_id}</span> · Auth: {user.auth_provider || "email"} · Signed up {fmtDate(user.created_at)}
      </div>
    </div>
  );
}


export default function Admin() {
  const [tab, setTab] = useState("overview");
  const [d, setD] = useState(null);
  const [platformStats, setPlatformStats] = useState(null);
  const [err, setErr] = useState("");
  const [warming, setWarming] = useState(false);
  const [subs, setSubs] = useState([]);
  const [actions, setActions] = useState([]);
  const [acting, setActing] = useState("");
  const [expanded, setExpanded] = useState("");
  const [charges, setCharges] = useState({});
  const [amt, setAmt] = useState({});

  const loadSubs = () => api.get("/admin/subscribers").then((r) => setSubs(r.data.subscribers || [])).catch(() => {});
  const loadStats = () => api.get("/admin/stats").then((r) => setPlatformStats(r.data)).catch(() => {});
  const loadActions = () => api.get("/admin/actions?limit=25").then((r) => setActions(r.data.actions || [])).catch(() => {});

  const toggleHistory = async (u) => {
    if (expanded === u.user_id) { setExpanded(""); return; }
    setExpanded(u.user_id);
    if (!charges[u.user_id]) {
      try { const r = await api.get(`/admin/subscribers/${u.user_id}/charges`); setCharges((c) => ({ ...c, [u.user_id]: r.data.charges || [] })); }
      catch (e) { toast.error(e?.response?.data?.detail || "Could not load charges."); }
    }
  };

  useEffect(() => {
    api.get("/admin/overview").then((r) => setD(r.data)).catch((e) => setErr(e?.response?.status === 403 ? "Admin access required." : "Could not load admin data."));
    loadSubs();
    loadStats();
  }, []);

  const cancelSub = async (u, immediate) => {
    const msg = immediate
      ? `Cancel ${u.email}'s subscription IMMEDIATELY? They lose access right away.`
      : `Cancel ${u.email}'s subscription at the end of the period? They keep access until then, no renewal.`;
    if (!window.confirm(msg)) return;
    setActing(u.user_id);
    try { await api.post(`/admin/subscribers/${u.user_id}/cancel`, { immediate }); toast.success("Subscription canceled."); loadSubs(); loadActions(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not cancel."); }
    finally { setActing(""); }
  };

  const refundCharge = async (u, charge) => {
    const raw = amt[charge.id];
    const amount = raw === undefined || raw === "" ? null : Number(raw);
    const label = amount ? `${fmt(amount)}` : `the full remaining ${fmt(charge.refundable)}`;
    if (!window.confirm(`Refund ${label} on this charge for ${u.email}? This cannot be undone.`)) return;
    setActing(charge.id);
    try {
      const r = await api.post(`/admin/subscribers/${u.user_id}/refund`, { charge_id: charge.id, amount });
      toast.success(`Refunded ${fmt(r.data.amount)} (${r.data.status}).`);
      const rc = await api.get(`/admin/subscribers/${u.user_id}/charges`);
      setCharges((c) => ({ ...c, [u.user_id]: rc.data.charges || [] }));
      loadActions();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not refund."); }
    finally { setActing(""); }
  };

  const refundAndRevoke = async (u) => {
    const msg = `⚠️ REFUND & REVOKE ${u.email}?\n\nThis will:\n  1. Cancel their subscription IMMEDIATELY (they lose access)\n  2. Refund their most recent charge in FULL\n  3. Release their Founding seat (if any)\n\nThis cannot be undone. Continue?`;
    if (!window.confirm(msg)) return;
    const reason = window.prompt("Optional: reason for the record (chargeback, fraud, refund request, etc.)") || "";
    setActing(u.user_id);
    try {
      const r = await api.post(`/admin/subscribers/${u.user_id}/refund-and-revoke`, { reason });
      const parts = [];
      if (r.data.canceled) parts.push("subscription canceled");
      if (r.data.refunded) parts.push(`refunded ${fmt(r.data.refunded.amount)}`);
      if (r.data.founder_seat_released) parts.push(`released Founder #${String(r.data.released_founder_number).padStart(3, "0")}`);
      toast.success(parts.length ? parts.join(" · ") : "Action completed (see warnings).");
      if (r.data.warnings?.length) r.data.warnings.forEach((w) => toast.warning(w));
      loadSubs();
      loadStats();
      loadActions();
    } catch (e) { toast.error(e?.response?.data?.detail || "Refund & revoke failed."); }
    finally { setActing(""); }
  };

  const refreshFeed = async () => {
    setWarming(true);
    try {
      const r = await api.post("/admin/prewarm-sam");
      toast.success(r.data?.skipped ? "SAM key not set — nothing to warm." : `SAM feed refreshed — warmed ${r.data?.warmed ?? 0} combo(s).`);
      loadStats();
    } catch (e) {
      toast.error("Refresh failed — SAM.gov may be throttled. Try again after UTC reset.");
    } finally { setWarming(false); }
  };

  if (err) return <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-12 text-center text-slate-400" data-testid="admin-error">{err}</div>;
  if (!d) return <div className="h-64 flex items-center justify-center"><div className="h-8 w-8 border-[3px] border-slate-200 border-t-slate-900 rounded-full animate-spin" /></div>;

  const stats = [
    { label: "Total Users", value: d.total_users, icon: Users, color: "text-accent" },
    { label: "Profiles Completed", value: d.profiles_completed, icon: FileCheck2, color: "text-emerald-600" },
    { label: "Est. MRR", value: fmt(d.mrr), icon: DollarSign, color: "text-amber-600" },
    { label: "Total Bids Tracked", value: d.total_bids, icon: KanbanSquare, color: "text-slate-900" },
    { label: "Marketplace Providers", value: d.marketplace_providers, icon: Store, color: "text-accent" },
    { label: "Team Members Invited", value: d.team_members, icon: UserPlus, color: "text-emerald-600" },
    { label: "Org Waitlist", value: d.agency_waitlist_count ?? 0, icon: Building2, color: "text-amber-600" },
  ];
  const planTotal = Object.values(d.plan_counts).reduce((a, b) => a + b, 0) || 1;
  const planColor = { free: "bg-slate-300", growth: "bg-emerald-500", pro: "bg-accent", enterprise: "bg-emerald-900" };

  return (
    <div className="space-y-6" data-testid="admin-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Admin Dashboard</h1>
          <p className="text-slate-500 mt-1">Platform health at a glance.</p>
        </div>
        <button onClick={refreshFeed} disabled={warming} data-testid="admin-refresh-sam" className="inline-flex items-center gap-2 bg-emerald-900 text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-emerald-800 disabled:opacity-60 transition-colors">
          <RefreshCw className={`h-4 w-4 ${warming ? "animate-spin" : ""}`} /> {warming ? "Refreshing…" : "Refresh SAM feed now"}
        </button>
      </div>

      <div className="border-b border-slate-200 flex items-center gap-1 -mt-2" data-testid="admin-tabs">
        {[
          { id: "overview", label: "Overview", icon: LayoutDashboard },
          { id: "profiles", label: "User Profiles", icon: IdCard },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`admin-tab-${t.id}`}
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              tab === t.id
                ? "border-emerald-700 text-emerald-800"
                : "border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300"
            }`}
          >
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "profiles" ? (
        <ProfilesTab />
      ) : (
      <>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-5">
        {stats.map((s) => (
          <div key={s.label} className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 flex flex-col gap-2" data-testid={`admin-stat-${s.label.replace(/[^a-z]+/gi, "-").toLowerCase()}`}>
            <s.icon className={`h-5 w-5 ${s.color}`} />
            <span className="font-data text-3xl font-medium tracking-tight text-slate-900">{s.value}</span>
            <span className="text-sm text-slate-500">{s.label}</span>
          </div>
        ))}
      </div>

      <PlatformHealth stats={platformStats} onRefreshFeed={refreshFeed} warming={warming} />

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
          <h3 className="font-heading text-lg font-semibold mb-4 inline-flex items-center gap-2"><TrendingUp className="h-5 w-5" /> Plan distribution</h3>
          <div className="space-y-3">
            {Object.entries(d.plan_counts).map(([plan, count]) => (
              <div key={plan} data-testid={`plan-row-${plan}`}>
                <div className="flex justify-between text-sm mb-1">
                  <span className="capitalize text-slate-700 font-medium">{plan}</span>
                  <span className="font-data text-slate-500">{count}</span>
                </div>
                <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                  <div className={`h-full rounded-full ${planColor[plan] || "bg-slate-400"}`} style={{ width: `${(count / planTotal) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-5 pt-4 border-t border-slate-100 flex gap-4 text-sm">
            <span className="text-slate-500">Active subs: <span className="font-semibold text-emerald-600">{d.subscription_status.active || 0}</span></span>
            <span className="text-slate-500">Canceled: <span className="font-semibold text-slate-700">{d.subscription_status.canceled || 0}</span></span>
            <span className="text-slate-500">Expired: <span className="font-semibold text-slate-700">{d.subscription_status.expired || 0}</span></span>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
          <h3 className="font-heading text-lg font-semibold mb-4 inline-flex items-center gap-2"><KanbanSquare className="h-5 w-5" /> Bids by stage</h3>
          {Object.keys(d.bids_by_stage).length === 0 ? (
            <p className="text-sm text-slate-400">No bids tracked yet.</p>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(d.bids_by_stage).map(([stage, count]) => (
                <div key={stage} className="rounded-lg border border-slate-100 bg-slate-50 p-3" data-testid={`stage-${stage}`}>
                  <p className="font-data text-2xl font-medium text-slate-900">{count}</p>
                  <p className="text-xs text-slate-500 capitalize">{stage}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6" data-testid="admin-subscribers">
        <h3 className="font-heading text-lg font-semibold mb-4 inline-flex items-center gap-2"><CreditCard className="h-5 w-5" /> Subscriptions <span className="text-sm font-normal text-slate-400">({subs.length})</span></h3>
        {subs.length === 0 ? (
          <p className="text-sm text-slate-400">No subscriptions yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
                  <th className="py-2 pr-4 font-medium">Customer</th>
                  <th className="py-2 pr-4 font-medium">Plan</th>
                  <th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 pr-4 font-medium">Renews / Ends</th>
                  <th className="py-2 pr-4 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {subs.map((u) => {
                  const canceling = u.cancel_at_period_end;
                  const trialing = u.stripe_status === "trialing";
                  const active = u.subscription_status === "active";
                  const isOpen = expanded === u.user_id;
                  const uCharges = charges[u.user_id];
                  return (
                    <React.Fragment key={u.user_id}>
                    <tr data-testid={`sub-row-${u.user_id}`} className="hover:bg-slate-50/60 align-middle">
                      <td className="py-3 pr-4">
                        <p className="font-medium text-slate-900 truncate max-w-[200px]">{u.name || u.email}</p>
                        <p className="text-xs text-slate-400 truncate max-w-[200px]">{u.email}</p>
                      </td>
                      <td className="py-3 pr-4"><span className="text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 capitalize">{u.plan}</span></td>
                      <td className="py-3 pr-4">
                        <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                          trialing ? "bg-lime-100 text-emerald-800" : canceling ? "bg-amber-100 text-amber-700" : active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                          {trialing ? "Trialing" : canceling ? "Ending" : active ? "Active" : (u.subscription_status || "none")}
                        </span>
                      </td>
                      <td className="py-3 pr-4 text-slate-500 whitespace-nowrap"><span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5 text-slate-300" />{fmtDate(u.current_period_end) || "—"}</span></td>
                      <td className="py-3 pr-0 text-right whitespace-nowrap">
                        <div className="inline-flex items-center gap-1.5 justify-end flex-wrap">
                          {active && !canceling && (
                            <button onClick={() => cancelSub(u, false)} disabled={acting === u.user_id} data-testid={`cancel-eop-${u.user_id}`} className="text-xs font-medium rounded-md px-2.5 py-1.5 bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition-colors">Cancel (end of period)</button>
                          )}
                          {active && (
                            <button onClick={() => cancelSub(u, true)} disabled={acting === u.user_id} data-testid={`cancel-now-${u.user_id}`} className="text-xs font-medium rounded-md px-2.5 py-1.5 bg-white border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50 transition-colors inline-flex items-center gap-1"><XCircle className="h-3.5 w-3.5" /> Cancel now</button>
                          )}
                          <button onClick={() => refundAndRevoke(u)} disabled={acting === u.user_id} data-testid={`refund-revoke-${u.user_id}`} title="Cancel immediately + refund last charge + release founder seat" className="text-xs font-semibold rounded-md px-2.5 py-1.5 bg-red-600 border border-red-700 text-white hover:bg-red-700 disabled:opacity-50 transition-colors inline-flex items-center gap-1"><Undo2 className="h-3.5 w-3.5" /> Refund &amp; revoke</button>
                          <button onClick={() => toggleHistory(u)} data-testid={`history-${u.user_id}`} className="text-xs font-medium rounded-md px-2.5 py-1.5 bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors inline-flex items-center gap-1"><ReceiptText className="h-3.5 w-3.5" /> Billing history <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isOpen ? "rotate-180" : ""}`} /></button>
                        </div>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr data-testid={`charges-${u.user_id}`}>
                        <td colSpan={5} className="bg-slate-50/70 px-4 py-3">
                          {!uCharges ? (
                            <p className="text-xs text-slate-400">Loading charges…</p>
                          ) : uCharges.length === 0 ? (
                            <p className="text-xs text-slate-400">No charges found for this customer.</p>
                          ) : (
                            <div className="space-y-2">
                              {uCharges.map((c) => (
                                <div key={c.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2" data-testid={`charge-${c.id}`}>
                                  <div className="text-sm">
                                    <span className="font-medium text-slate-900">{fmt(c.amount)}</span>
                                    <span className="text-slate-400 text-xs ml-2">{fmtDate(c.created)}</span>
                                    <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${c.refunded ? "bg-slate-100 text-slate-500" : c.amount_refunded > 0 ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                                      {c.refunded ? "Refunded" : c.amount_refunded > 0 ? `Partially refunded ${fmt(c.amount_refunded)}` : c.status}
                                    </span>
                                  </div>
                                  {c.refundable > 0 ? (
                                    <div className="flex items-center gap-2">
                                      <div className="relative">
                                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">$</span>
                                        <input data-testid={`refund-amt-${c.id}`} type="number" step="0.01" min="0" max={c.refundable} placeholder={String(c.refundable)} value={amt[c.id] ?? ""} onChange={(e) => setAmt((a) => ({ ...a, [c.id]: e.target.value }))} className="w-24 rounded-md border border-slate-200 pl-5 pr-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-accent/40" />
                                      </div>
                                      <button onClick={() => refundCharge(u, c)} disabled={acting === c.id} data-testid={`refund-charge-${c.id}`} className="text-xs font-medium rounded-md px-2.5 py-1.5 bg-white border border-amber-200 text-amber-700 hover:bg-amber-50 disabled:opacity-50 transition-colors inline-flex items-center gap-1"><Undo2 className="h-3.5 w-3.5" /> Refund</button>
                                    </div>
                                  ) : (
                                    <span className="text-xs text-slate-400">Fully refunded</span>
                                  )}
                                </div>
                              ))}
                              <p className="text-[11px] text-slate-400">Leave the amount blank to refund the full remaining balance, or enter a smaller amount for a partial refund.</p>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ActionsLog actions={actions} onReload={loadActions} />

      <div className="grid lg:grid-cols-1 gap-6">
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg font-semibold">Recent signups</h3>
            <span className="text-xs text-slate-400">{d.recent_users.length} latest</span>
          </div>
          <div className="overflow-x-auto -mx-2">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wider text-slate-400 border-b border-slate-200">
                  <th className="text-left font-medium py-2 px-2">User</th>
                  <th className="text-left font-medium py-2 px-2">Plan</th>
                  <th className="text-left font-medium py-2 px-2">Status</th>
                  <th className="text-left font-medium py-2 px-2">Signed up</th>
                  <th className="text-left font-medium py-2 px-2">Renews</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {d.recent_users.map((u, i) => {
                  const isActive = u.subscription_status === "active";
                  const isTrial = u.stripe_status === "trialing" || (u.trial_status === "pending" && !isActive);
                  const statusLabel = isActive ? (u.stripe_status === "trialing" ? "Trialing" : "Active")
                    : u.subscription_status === "canceled" ? "Canceled"
                    : u.subscription_status === "expired" ? "Expired"
                    : isTrial ? "Trial started" : "Free";
                  const statusColor = isActive ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : u.subscription_status === "canceled" || u.subscription_status === "expired" ? "bg-red-50 text-red-700 border-red-200"
                    : isTrial ? "bg-amber-50 text-amber-700 border-amber-200"
                    : "bg-slate-100 text-slate-500 border-slate-200";
                  const interval = u.stripe_interval === "year" ? "annual" : u.stripe_interval === "month" ? "monthly" : "";
                  return (
                    <tr key={u.email || i} data-testid={`recent-user-${i}`} className="align-middle">
                      <td className="py-3 px-2 min-w-0 max-w-[220px]">
                        <p className="text-sm font-medium text-slate-900 truncate" title={u.name}>{u.name || u.email}</p>
                        <p className="text-xs text-slate-400 truncate" title={u.email}>{u.email}</p>
                      </td>
                      <td className="py-3 px-2">
                        <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 capitalize">{u.plan || "free"}</span>
                        {interval && <p className="text-[10px] text-slate-400 mt-0.5">{interval}</p>}
                        {u.is_founder && (
                          <p className="text-[10px] text-lime-700 font-semibold mt-0.5">
                            Founder #{String(u.founder_number || 0).padStart(3, "0")}
                          </p>
                        )}
                      </td>
                      <td className="py-3 px-2">
                        <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full border ${statusColor}`}>{statusLabel}</span>
                      </td>
                      <td className="py-3 px-2 text-xs text-slate-500 whitespace-nowrap" title={u.created_at}>{fmtDate(u.created_at)}</td>
                      <td className="py-3 px-2 text-xs text-slate-500 whitespace-nowrap" title={u.current_period_end}>{u.current_period_end ? fmtDate(u.current_period_end) : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
          <h3 className="font-heading text-lg font-semibold mb-4">Recent payments</h3>
          {d.recent_payments.length === 0 ? (
            <p className="text-sm text-slate-400">No payments yet.</p>
          ) : (
            <div className="divide-y divide-slate-100">
              {d.recent_payments.map((p, i) => (
                <div key={`${p.email}-${i}`} className="flex items-center justify-between py-2.5" data-testid={`recent-payment-${i}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{p.email}</p>
                    <p className="text-xs text-slate-400 capitalize">{p.plan}</p>
                  </div>
                  <span className="font-data text-sm font-medium text-emerald-600 shrink-0">{fmt(p.amount)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6" data-testid="admin-waitlist">
        <h3 className="font-heading text-lg font-semibold mb-4 inline-flex items-center gap-2"><Building2 className="h-5 w-5" /> Issuing-organization waitlist <span className="text-sm font-normal text-slate-400">({d.agency_waitlist_count ?? 0})</span></h3>
        {(!d.agency_waitlist || d.agency_waitlist.length === 0) ? (
          <p className="text-sm text-slate-400">No signups yet. Share the <a href="/for-agencies" className="text-emerald-700 font-medium hover:underline">For Issuing Organizations</a> page to start collecting leads.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
                  <th className="py-2 pr-4 font-medium">Name</th>
                  <th className="py-2 pr-4 font-medium">Email</th>
                  <th className="py-2 pr-4 font-medium">Organization</th>
                  <th className="py-2 pr-4 font-medium">Type</th>
                  <th className="py-2 pr-4 font-medium">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {d.agency_waitlist.map((w, i) => (
                  <tr key={w.email || i} data-testid={`waitlist-row-${i}`} className="hover:bg-slate-50/60">
                    <td className="py-2.5 pr-4 font-medium text-slate-900">{w.name || "—"}</td>
                    <td className="py-2.5 pr-4 text-slate-600"><a href={`mailto:${w.email}`} className="inline-flex items-center gap-1 hover:text-emerald-700"><Mail className="h-3.5 w-3.5" />{w.email}</a></td>
                    <td className="py-2.5 pr-4 text-slate-600">{w.organization || "—"}</td>
                    <td className="py-2.5 pr-4"><span className="text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 capitalize">{w.org_type || "—"}</span></td>
                    <td className="py-2.5 pr-4 text-slate-400 whitespace-nowrap">{fmtDate(w.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      </>
      )}
    </div>
  );
}
